import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Package, FileText, Globe, Receipt, LogOut, Plus, Pencil, Trash2, Box, Home, FileCheck, Settings as SettingsIcon, Copy, ExternalLink, RefreshCw, Mail, BarChart3, TrendingUp, Users, DollarSign } from 'lucide-react';
import EmailTemplatesSection from '@/components/EmailTemplatesSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [packages, setPackages] = useState([]);
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [countryRequirements, setCountryRequirements] = useState([]);
  const [productTerms, setProductTerms] = useState([]);
  const [pageTerms, setPageTerms] = useState(null);
  const [settings, setSettings] = useState(null);
  const [termsAcceptances, setTermsAcceptances] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Dashboard analytics state
  const [dashboardData, setDashboardData] = useState(null);
  
  // Transactions page state
  const [transactionsSubTab, setTransactionsSubTab] = useState('list'); // list, upcoming, users
  const [filterOptions, setFilterOptions] = useState({ packages: [], user_countries: [], ip_countries: [] });
  const [transactionFilters, setTransactionFilters] = useState({
    package_id: 'all',
    user_country: 'all',
    ip_country: 'all',
    date_from: '',
    date_to: ''
  });
  const [upcomingPayments, setUpcomingPayments] = useState([]);
  const [upcomingDays, setUpcomingDays] = useState(30);
  const [usersByStatus, setUsersByStatus] = useState([]);
  const [usersStatusFilter, setUsersStatusFilter] = useState('all'); // all, active, inactive
  
  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [configuredLanguages, setConfiguredLanguages] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  
  // Package form
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [showUpdateSubscriptionsDialog, setShowUpdateSubscriptionsDialog] = useState(false);
  const [updatingPackage, setUpdatingPackage] = useState(null);
  const [updateResults, setUpdateResults] = useState(null);
  const [packageForm, setPackageForm] = useState({
    name: '',
    product_name: '',
    description: '',
    features: '',
    monthly_price: '',
    yearly_price: '',
    transaction_cost: '0'
  });

  // Product form
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: ''
  });

  // Product Terms form
  const [showProductTermsDialog, setShowProductTermsDialog] = useState(false);
  const [productTermsForm, setProductTermsForm] = useState({
    product_id: '',
    content: ''
  });

  // Page Terms form
  const [showPageTermsDialog, setShowPageTermsDialog] = useState(false);
  const [pageTermsForm, setPageTermsForm] = useState({
    content: ''
  });

  // Settings form
  const [settingsForm, setSettingsForm] = useState({
    stripe_publishable_key: '',
    stripe_secret_key: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
    // Company details
    company_name: '',
    company_logo_url: '',
    company_street: '',
    company_city: '',
    company_state: '',
    company_zip: '',
    company_country: '',
    company_email: '',
    company_phone: '',
    company_tax_id: ''
  });

  // Country requirement form
  const [showCountryDialog, setShowCountryDialog] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [countryForm, setCountryForm] = useState({
    country_code: '',
    country_name: '',
    language_code: 'en',
    language_name: 'English',
    requires_digital_id: false,
    digital_id_type: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchAllData();
  }, [navigate]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  const fetchAllData = async () => {
    try {
      const [pkgsRes, prodsRes, transRes, subsRes, countriesRes, prodTermsRes, pageTermsRes, settingsRes, termsAcceptRes, emailTemplatesRes, configuredLangsRes, dashboardRes] = await Promise.all([
        axios.get(`${API}/admin/packages`, getAuthHeaders()),
        axios.get(`${API}/admin/products`, getAuthHeaders()),
        axios.get(`${API}/admin/transactions`, getAuthHeaders()),
        axios.get(`${API}/admin/subscriptions`, getAuthHeaders()),
        axios.get(`${API}/admin/country-requirements`, getAuthHeaders()),
        axios.get(`${API}/admin/terms`, getAuthHeaders()),
        axios.get(`${API}/admin/page-terms`, getAuthHeaders()).catch(() => ({ data: null })),
        axios.get(`${API}/admin/settings`, getAuthHeaders()).catch(() => ({ data: null })),
        axios.get(`${API}/admin/page-terms-acceptances`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/email-templates`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/configured-languages`, getAuthHeaders()).catch(() => ({ data: [{code: 'en', name: 'English'}] })),
        axios.get(`${API}/admin/dashboard-analytics`, getAuthHeaders()).catch(() => ({ data: null }))
      ]);
      setPackages(pkgsRes.data);
      setProducts(prodsRes.data);
      setTransactions(transRes.data);
      setSubscriptions(subsRes.data);
      setCountryRequirements(countriesRes.data);
      setProductTerms(prodTermsRes.data);
      setPageTerms(pageTermsRes.data);
      setTermsAcceptances(termsAcceptRes.data);
      setEmailTemplates(emailTemplatesRes.data);
      setConfiguredLanguages(configuredLangsRes.data);
      if (pageTermsRes.data) {
        setPageTermsForm({ content: pageTermsRes.data.content });
      }
      setSettings(settingsRes.data);
      setDashboardData(dashboardRes.data);
      if (settingsRes.data) {
        setSettingsForm({
          stripe_publishable_key: settingsRes.data.stripe_publishable_key || '',
          stripe_secret_key: settingsRes.data.stripe_secret_key || '',
          smtp_host: settingsRes.data.smtp_host || '',
          smtp_port: settingsRes.data.smtp_port || 587,
          smtp_username: settingsRes.data.smtp_username || '',
          smtp_password: settingsRes.data.smtp_password || '',
          smtp_from_email: settingsRes.data.smtp_from_email || '',
          smtp_from_name: settingsRes.data.smtp_from_name || '',
          // Company details
          company_name: settingsRes.data.company_name || '',
          company_logo_url: settingsRes.data.company_logo_url || '',
          company_street: settingsRes.data.company_street || '',
          company_city: settingsRes.data.company_city || '',
          company_state: settingsRes.data.company_state || '',
          company_zip: settingsRes.data.company_zip || '',
          company_country: settingsRes.data.company_country || '',
          company_email: settingsRes.data.company_email || '',
          company_phone: settingsRes.data.company_phone || '',
          company_tax_id: settingsRes.data.company_tax_id || ''
        });
      }
    } catch (error) {
      toast.error('Failed to load data');
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    }
  };


  // Fetch filter options for transactions
  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/admin/filter-options`, getAuthHeaders());
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  // Fetch filtered transactions
  const fetchFilteredTransactions = async () => {
    try {
      const params = new URLSearchParams();
      if (transactionFilters.package_id && transactionFilters.package_id !== 'all') {
        params.append('package_id', transactionFilters.package_id);
      }
      if (transactionFilters.user_country && transactionFilters.user_country !== 'all') {
        params.append('user_country', transactionFilters.user_country);
      }
      if (transactionFilters.ip_country && transactionFilters.ip_country !== 'all') {
        params.append('ip_country', transactionFilters.ip_country);
      }
      if (transactionFilters.date_from) params.append('date_from', transactionFilters.date_from);
      if (transactionFilters.date_to) params.append('date_to', transactionFilters.date_to);
      
      const response = await axios.get(`${API}/admin/transactions?${params.toString()}`, getAuthHeaders());
      setTransactions(response.data);
    } catch (error) {
      toast.error('Failed to fetch transactions');
    }
  };

  // Fetch upcoming payments
  const fetchUpcomingPayments = async () => {
    try {
      const response = await axios.get(`${API}/admin/upcoming-payments?days=${upcomingDays}`, getAuthHeaders());
      setUpcomingPayments(response.data);
    } catch (error) {
      toast.error('Failed to fetch upcoming payments');
    }
  };

  // Fetch users by subscription status
  const fetchUsersByStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/users-by-subscription-status?status=${usersStatusFilter}`, getAuthHeaders());
      setUsersByStatus(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  // Apply filters
  const applyFilters = () => {
    fetchFilteredTransactions();
  };

  // Clear filters
  const clearFilters = () => {
    setTransactionFilters({
      package_id: 'all',
      user_country: 'all',
      ip_country: 'all',
      date_from: '',
      date_to: ''
    });
    fetchAllData(); // Reload all data
  };

  // Effect to load filter options when transactions tab is active
  useEffect(() => {
    if (activeTab === 'transactions' && filterOptions.packages.length === 0) {
      fetchFilterOptions();
    }
    // eslint-disable-next-line
  }, [activeTab]);

  // Effect to fetch upcoming payments when tab or days changes
  useEffect(() => {
    if (transactionsSubTab === 'upcoming') {
      fetchUpcomingPayments();
    }
    // eslint-disable-next-line
  }, [transactionsSubTab, upcomingDays]);

  // Effect to fetch users when tab or status changes
  useEffect(() => {
    if (transactionsSubTab === 'users') {
      fetchUsersByStatus();
    }
    // eslint-disable-next-line
  }, [transactionsSubTab, usersStatusFilter]);


  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin/login');
  };

  const copyPublicLink = (productName) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/packages?product=${encodeURIComponent(productName)}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard!');
  };

  // Product handlers
  const handleCreateProduct = async () => {
    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productForm, getAuthHeaders());
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/admin/products`, productForm, getAuthHeaders());
        toast.success('Product created successfully');
      }
      setShowProductDialog(false);
      setEditingProduct(null);
      setProductForm({ name: '', description: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleEditProduct = (prod) => {
    setEditingProduct(prod);
    setProductForm({
      name: prod.name,
      description: prod.description
    });
    setShowProductDialog(true);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, getAuthHeaders());
      toast.success('Product deleted successfully');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  // Package handlers
  const handleCreatePackage = async () => {
    try {
      const features = packageForm.features.split('\n').filter(f => f.trim());
      const data = {
        ...packageForm,
        features,
        monthly_price: parseFloat(packageForm.monthly_price),
        yearly_price: parseFloat(packageForm.yearly_price),
        transaction_cost: parseFloat(packageForm.transaction_cost || 0)
      };

      if (editingPackage) {
        await axios.put(`${API}/admin/packages/${editingPackage.id}`, data, getAuthHeaders());
        toast.success('Package updated successfully');
      } else {
        await axios.post(`${API}/admin/packages`, data, getAuthHeaders());
        toast.success('Package created successfully');
      }

      setShowPackageDialog(false);
      setEditingPackage(null);
      setPackageForm({
        name: '',
        product_name: '',
        description: '',
        features: '',
        monthly_price: '',
        yearly_price: '',
        transaction_cost: '0'
      });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save package');
    }
  };

  const handleEditPackage = (pkg) => {
    setEditingPackage(pkg);
    setPackageForm({
      name: pkg.name,
      product_name: pkg.product_name,
      description: pkg.description,
      features: pkg.features.join('\n'),
      monthly_price: pkg.monthly_price.toString(),
      yearly_price: pkg.yearly_price.toString(),
      transaction_cost: (pkg.transaction_cost || 0).toString()
    });
    setShowPackageDialog(true);
  };

  const handleUpdateSubscriptions = async () => {
    if (!updatingPackage) return;
    
    try {
      const response = await axios.post(
        `${API}/admin/packages/${updatingPackage.id}/update-subscriptions`,
        {},
        getAuthHeaders()
      );
      
      setUpdateResults(response.data);
      
      if (response.data.updated_count > 0) {
        toast.success(`Updated ${response.data.updated_count} subscriptions successfully!`);
      } else {
        toast.info(response.data.message);
      }
      
      if (response.data.failed_count > 0) {
        toast.warning(`${response.data.failed_count} subscriptions failed to update`);
      }
      
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscriptions');
    }
  };

  const handleDeletePackage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;
    try {
      await axios.delete(`${API}/admin/packages/${id}`, getAuthHeaders());
      toast.success('Package deleted successfully');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  // Product Terms handlers
  const handleSaveProductTerms = async () => {
    try {
      await axios.post(`${API}/admin/terms`, productTermsForm, getAuthHeaders());
      toast.success('Product terms saved successfully');
      setShowProductTermsDialog(false);
      setProductTermsForm({ product_id: '', content: '' });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save product terms');
    }
  };

  const handleDeleteProductTerms = async (termsId) => {
    if (!window.confirm('Are you sure you want to delete these terms?')) return;
    try {
      await axios.delete(`${API}/admin/terms/${termsId}`, getAuthHeaders());
      toast.success('Product terms deleted successfully');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete product terms');
    }
  };

  // Page Terms handlers
  const handleSavePageTerms = async () => {
    try {
      await axios.post(`${API}/admin/page-terms`, pageTermsForm, getAuthHeaders());
      toast.success('Page terms saved successfully');
      setShowPageTermsDialog(false);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save page terms');
    }
  };

  // Settings handlers
  const handleSaveSettings = async () => {
    try {
      await axios.post(`${API}/admin/settings`, settingsForm, getAuthHeaders());
      toast.success('Settings saved successfully');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  // Email template handlers
  const handleInitializeTemplates = async () => {
    try {
      // First refresh the data to get latest countries
      await fetchAllData();
      
      // Then initialize templates
      const response = await axios.post(`${API}/admin/email-templates/initialize-defaults`, {}, getAuthHeaders());
      toast.success(response.data.message);
      
      // Refresh again to show new templates
      await fetchAllData();
    } catch (error) {
      toast.error('Failed to sync templates');
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setShowTemplateDialog(true);
  };

  const handleSaveTemplate = async () => {
    try {
      await axios.put(`${API}/admin/email-templates/${editingTemplate.id}`, {
        subject: editingTemplate.subject,
        body: editingTemplate.body
      }, getAuthHeaders());
      toast.success('Template saved successfully');
      setShowTemplateDialog(false);
      setEditingTemplate(null);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handlePreviewTemplate = (template) => {
    // Replace template variables with sample data for preview
    const sampleData = {
      user_name: 'John Doe',
      user_email: 'john.doe@example.com',
      package_name: 'Professional Plan',
      amount: '99.00',
      currency: 'USD',
      billing_cycle: 'monthly',
      old_price: '79.00',
      new_price: '99.00',
      invoice_date: new Date().toLocaleDateString(),
      company_name: settingsForm.company_name || 'Your Company'
    };

    let previewSubject = template.subject;
    let previewBody = template.body;

    Object.keys(sampleData).forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      previewSubject = previewSubject.replace(regex, sampleData[key]);
      previewBody = previewBody.replace(regex, sampleData[key]);
    });

    setPreviewTemplate({
      ...template,
      subject: previewSubject,
      body: previewBody
    });
    setShowPreviewDialog(true);
  };

  // Country requirement handlers
  const handleEditCountry = (country) => {
    setEditingCountry(country);
    setCountryForm({
      country_code: country.country_code,
      country_name: country.country_name,
      language_code: country.language_code || 'en',
      language_name: country.language_name || 'English',
      requires_digital_id: country.requires_digital_id || false,
      digital_id_type: country.digital_id_type || ''
    });
    setShowCountryDialog(true);
  };

  const handleDeleteCountry = async (countryId) => {
    if (!window.confirm('Are you sure you want to delete this country? This will affect email template languages.')) return;
    try {
      await axios.delete(`${API}/admin/country-requirements/${countryId}`, getAuthHeaders());
      toast.success('Country requirement deleted successfully');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete country requirement');
    }
  };

  const handleSaveCountryRequirement = async () => {
    try {
      if (editingCountry) {
        // Update existing country
        await axios.put(`${API}/admin/country-requirements/${editingCountry.id}`, countryForm, getAuthHeaders());
        toast.success('Country requirement updated successfully');
      } else {
        // Create new country
        await axios.post(`${API}/admin/country-requirements`, countryForm, getAuthHeaders());
        toast.success('Country requirement saved successfully');
      }
      setShowCountryDialog(false);
      setEditingCountry(null);
      setCountryForm({
        country_code: '',
        country_name: '',
        language_code: 'en',
        language_name: 'English',
        requires_digital_id: false,
        digital_id_type: ''
      });
      fetchAllData();
    } catch (error) {
      toast.error(editingCountry ? 'Failed to update country requirement' : 'Failed to save country requirement');
    }
  };

  const getProductPackages = (productName) => {
    return packages.filter(pkg => pkg.product_name === productName);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Dashboard</h2>
                <p className="text-gray-600 mt-1">Overview of your subscription platform</p>
              </div>
            </div>

            {!dashboardData ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Loading dashboard data...</p>
              </div>
            ) : (
              <>
                {/* Key Metrics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Collected This Month</p>
                          <p className="text-2xl font-bold text-gray-900 mt-2">
                            ${dashboardData.amount_collected_this_month.toFixed(2)}
                          </p>
                        </div>
                        <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                          <DollarSign className="h-6 w-6 text-green-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Expected Next Month</p>
                          <p className="text-2xl font-bold text-gray-900 mt-2">
                            ${dashboardData.amount_to_collect_next_month.toFixed(2)}
                          </p>
                        </div>
                        <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <TrendingUp className="h-6 w-6 text-blue-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Total Users</p>
                          <p className="text-2xl font-bold text-gray-900 mt-2">
                            {dashboardData.total_users}
                          </p>
                        </div>
                        <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Users className="h-6 w-6 text-purple-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Active Subscriptions</p>
                          <p className="text-2xl font-bold text-gray-900 mt-2">
                            {dashboardData.additional_stats.active_subscriptions}
                          </p>
                        </div>
                        <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                          <Package className="h-6 w-6 text-orange-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Additional Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Total Revenue</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-green-600">
                        ${dashboardData.additional_stats.total_revenue.toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        From {dashboardData.additional_stats.paid_transactions} paid transactions
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Total Subscriptions</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-blue-600">
                        {dashboardData.additional_stats.total_subscriptions}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        {dashboardData.additional_stats.active_subscriptions} currently active
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Avg. Subscription Value</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-purple-600">
                        ${dashboardData.additional_stats.average_subscription_value.toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        Average per transaction
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Most Promising Packages */}
                <Card>
                  <CardHeader>
                    <CardTitle>Most Promising Packages</CardTitle>
                    <CardDescription>Packages with the most active subscriptions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {dashboardData.most_promising_packages.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No packages with subscriptions yet.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-3 px-4 font-semibold text-gray-700">Package Name</th>
                              <th className="text-left py-3 px-4 font-semibold text-gray-700">Product</th>
                              <th className="text-center py-3 px-4 font-semibold text-gray-700">Subscriptions</th>
                              <th className="text-right py-3 px-4 font-semibold text-gray-700">Monthly Price</th>
                              <th className="text-right py-3 px-4 font-semibold text-gray-700">Yearly Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dashboardData.most_promising_packages.map((pkg, idx) => (
                              <tr key={pkg.package_id} className="border-b hover:bg-gray-50">
                                <td className="py-3 px-4">{pkg.package_name}</td>
                                <td className="py-3 px-4 text-gray-600">{pkg.product_name}</td>
                                <td className="py-3 px-4 text-center">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                    {pkg.subscription_count}
                                  </span>
                                </td>
                                <td className="py-3 px-4 text-right">${pkg.monthly_price}</td>
                                <td className="py-3 px-4 text-right">${pkg.yearly_price}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Revenue by Country */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Revenue by Country</CardTitle>
                      <CardDescription>Total collected amounts by region</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {dashboardData.amounts_by_country.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">No transaction data yet.</p>
                      ) : (
                        <div className="space-y-4">
                          {dashboardData.amounts_by_country.slice(0, 10).map((countryData) => (
                            <div key={countryData.country} className="flex justify-between items-center">
                              <div className="flex items-center gap-2">
                                <Globe className="w-4 h-4 text-gray-400" />
                                <span className="font-medium text-gray-700">{countryData.country}</span>
                                <span className="text-sm text-gray-500">({countryData.transaction_count} transactions)</span>
                              </div>
                              <span className="font-semibold text-gray-900">
                                ${countryData.amount.toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Revenue by Product */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Revenue by Product</CardTitle>
                      <CardDescription>Total collected amounts by product</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {dashboardData.amounts_by_product.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">No transaction data yet.</p>
                      ) : (
                        <div className="space-y-4">
                          {dashboardData.amounts_by_product.map((productData) => (
                            <div key={productData.product_name} className="flex justify-between items-center">
                              <div className="flex items-center gap-2">
                                <Box className="w-4 h-4 text-gray-400" />
                                <span className="font-medium text-gray-700">{productData.product_name}</span>
                                <span className="text-sm text-gray-500">({productData.transaction_count} transactions)</span>
                              </div>
                              <span className="font-semibold text-gray-900">
                                ${productData.amount.toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Subscriptions by Region */}
                <Card>
                  <CardHeader>
                    <CardTitle>Subscriptions by Region</CardTitle>
                    <CardDescription>Package subscriptions breakdown by country</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {dashboardData.subscriptions_by_region.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No subscription data yet.</p>
                    ) : (
                      <div className="space-y-6">
                        {dashboardData.subscriptions_by_region.map((region) => (
                          <div key={region.country} className="border-l-4 border-blue-500 pl-4">
                            <div className="flex justify-between items-center mb-3">
                              <h4 className="font-semibold text-lg text-gray-900">{region.country}</h4>
                              <span className="text-sm font-medium text-gray-600">
                                {region.total_subscriptions} total subscriptions
                              </span>
                            </div>
                            <div className="space-y-2">
                              {region.packages.map((pkg, idx) => (
                                <div key={idx} className="flex justify-between items-center text-sm bg-gray-50 p-2 rounded">
                                  <span className="text-gray-700">{pkg.package_name}</span>
                                  <span className="font-medium text-gray-900">{pkg.count} subscriptions</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        );

      case 'products':
        return (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Products</CardTitle>
                  <CardDescription>Manage your products</CardDescription>
                </div>
                <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-product-button" onClick={() => { setEditingProduct(null); setProductForm({ name: '', description: '' }); }}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Product
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingProduct ? 'Edit Product' : 'Create Product'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Product Name</Label>
                        <Input
                          data-testid="product-name-input"
                          value={productForm.name}
                          onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                          placeholder="My Product"
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          data-testid="product-description-input"
                          value={productForm.description}
                          onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                          placeholder="Product description"
                        />
                      </div>
                      <Button data-testid="save-product-button" onClick={handleCreateProduct} className="w-full">
                        {editingProduct ? 'Update Product' : 'Create Product'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {products.map((prod) => (
                  <Card key={prod.id}>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-lg font-semibold">{prod.name}</h3>
                          <p className="text-gray-600 text-sm mt-1">{prod.description}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleEditProduct(prod)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDeleteProduct(prod.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {products.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No products yet. Create your first product!</p>
                )}
              </div>
            </CardContent>
          </Card>
        );

      case 'packages':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Subscription Packages by Product</CardTitle>
              <CardDescription>View and manage packages organized by products</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {products.map((product) => {
                  const productPackages = getProductPackages(product.name);
                  return (
                    <Card key={product.id} className="border-2">
                      <CardHeader className="bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-xl">{product.name}</CardTitle>
                            <CardDescription>{product.description}</CardDescription>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copyPublicLink(product.name)}
                              data-testid={`copy-link-${product.id}`}
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              Copy Link
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(`/packages?product=${encodeURIComponent(product.name)}`, '_blank')}
                              data-testid={`view-public-${product.id}`}
                            >
                              <ExternalLink className="w-4 h-4 mr-2" />
                              View Public
                            </Button>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button size="sm" data-testid={`add-package-to-${product.id}`} onClick={() => {
                                  setEditingPackage(null);
                                  setPackageForm({
                                    name: '',
                                    product_name: product.name,
                                    description: '',
                                    features: '',
                                    monthly_price: '',
                                    yearly_price: '',
                                    transaction_cost: '0'
                                  });
                                }}>
                                  <Plus className="w-4 h-4 mr-2" />
                                  Add Package
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-2xl">
                                <DialogHeader>
                                  <DialogTitle>Add Package to {product.name}</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 mt-4">
                                  <div>
                                    <Label>Package Name</Label>
                                    <Input
                                      data-testid="package-name-input"
                                      value={packageForm.name}
                                      onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                                      placeholder="Premium Plan"
                                    />
                                  </div>
                                  <div>
                                    <Label>Description</Label>
                                    <Textarea
                                      data-testid="package-description-input"
                                      value={packageForm.description}
                                      onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
                                      placeholder="Best for growing businesses"
                                    />
                                  </div>
                                  <div>
                                    <Label>Features (one per line)</Label>
                                    <Textarea
                                      data-testid="package-features-input"
                                      value={packageForm.features}
                                      onChange={(e) => setPackageForm({ ...packageForm, features: e.target.value })}
                                      placeholder="Unlimited users\n24/7 support\nAdvanced analytics"
                                      rows={5}
                                    />
                                  </div>
                                  <div className="grid grid-cols-3 gap-4">
                                    <div>
                                      <Label>Monthly Price ($)</Label>
                                      <Input
                                        data-testid="monthly-price-input"
                                        type="number"
                                        step="0.01"
                                        value={packageForm.monthly_price}
                                        onChange={(e) => setPackageForm({ ...packageForm, monthly_price: e.target.value })}
                                        placeholder="29.99"
                                      />
                                    </div>
                                    <div>
                                      <Label>Yearly Price ($)</Label>
                                      <Input
                                        data-testid="yearly-price-input"
                                        type="number"
                                        step="0.01"
                                        value={packageForm.yearly_price}
                                        onChange={(e) => setPackageForm({ ...packageForm, yearly_price: e.target.value })}
                                        placeholder="299.99"
                                      />
                                    </div>
                                    <div>
                                      <Label>Transaction Cost ($)</Label>
                                      <Input
                                        data-testid="transaction-cost-input"
                                        type="number"
                                        step="0.01"
                                        value={packageForm.transaction_cost}
                                        onChange={(e) => setPackageForm({ ...packageForm, transaction_cost: e.target.value })}
                                        placeholder="2.50"
                                      />
                                    </div>
                                  </div>
                                  <Button data-testid="save-package-button" onClick={handleCreatePackage} className="w-full">
                                    Create Package
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-6">
                        {productPackages.length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {productPackages.map((pkg) => (
                              <Card key={pkg.id} className="border">
                                <CardContent className="p-4">
                                  <div className="space-y-3">
                                    <div>
                                      <h4 className="font-bold text-lg">{pkg.name}</h4>
                                      <p className="text-sm text-gray-600">{pkg.description}</p>
                                    </div>
                                    <div className="flex gap-2 text-sm">
                                      <span className="font-medium">Monthly: <span className="text-indigo-600">${pkg.monthly_price.toFixed(2)}</span></span>
                                      <span className="font-medium">Yearly: <span className="text-indigo-600">${pkg.yearly_price.toFixed(2)}</span></span>
                                    </div>
                                    {pkg.transaction_cost > 0 && (
                                      <p className="text-xs text-amber-600">+ ${pkg.transaction_cost.toFixed(2)} fee</p>
                                    )}
                                    <div className="text-xs text-gray-600">
                                      <strong>Features:</strong>
                                      <ul className="list-disc list-inside mt-1">
                                        {pkg.features.slice(0, 3).map((f, i) => (
                                          <li key={i}>{f}</li>
                                        ))}
                                        {pkg.features.length > 3 && <li>+{pkg.features.length - 3} more...</li>}
                                      </ul>
                                    </div>
                                    <div className="flex gap-2 pt-2">
                                      <Button size="sm" variant="outline" onClick={() => handleEditPackage(pkg)} className="flex-1">
                                        <Pencil className="w-3 h-3" />
                                      </Button>
                                      <Button size="sm" variant="destructive" onClick={() => handleDeletePackage(pkg.id)} className="flex-1">
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                    <Button 
                                      size="sm" 
                                      variant="secondary" 
                                      onClick={() => {
                                        setUpdatingPackage(pkg);
                                        setUpdateResults(null);
                                        setShowUpdateSubscriptionsDialog(true);
                                      }}
                                      className="w-full mt-2"
                                      data-testid={`update-subs-${pkg.id}`}
                                    >
                                      <RefreshCw className="w-3 h-3 mr-1" />
                                      Update Existing Subscriptions
                                    </Button>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        ) : (
                          <p className="text-center text-gray-500 py-8">No packages for this product yet. Click "Add Package" to create one.</p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
                {products.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No products yet. Create products first in the Products tab!</p>
                )}
              </div>

              {/* Edit Package Dialog */}
              <Dialog open={showPackageDialog} onOpenChange={setShowPackageDialog}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Edit Package</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Package Name</Label>
                      <Input
                        value={packageForm.name}
                        onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                        placeholder="Premium Plan"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={packageForm.description}
                        onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
                        placeholder="Best for growing businesses"
                      />
                    </div>
                    <div>
                      <Label>Features (one per line)</Label>
                      <Textarea
                        value={packageForm.features}
                        onChange={(e) => setPackageForm({ ...packageForm, features: e.target.value })}
                        placeholder="Unlimited users\n24/7 support\nAdvanced analytics"
                        rows={5}
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Monthly Price ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={packageForm.monthly_price}
                          onChange={(e) => setPackageForm({ ...packageForm, monthly_price: e.target.value })}
                          placeholder="29.99"
                        />
                      </div>
                      <div>
                        <Label>Yearly Price ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={packageForm.yearly_price}
                          onChange={(e) => setPackageForm({ ...packageForm, yearly_price: e.target.value })}
                          placeholder="299.99"
                        />
                      </div>
                      <div>
                        <Label>Transaction Cost ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={packageForm.transaction_cost}
                          onChange={(e) => setPackageForm({ ...packageForm, transaction_cost: e.target.value })}
                          placeholder="2.50"
                        />
                      </div>
                    </div>
                    <Button onClick={handleCreatePackage} className="w-full">
                      Update Package
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Update Existing Subscriptions Dialog */}
              <Dialog open={showUpdateSubscriptionsDialog} onOpenChange={setShowUpdateSubscriptionsDialog}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Update Existing Subscriptions</DialogTitle>
                    <DialogDescription>
                      Apply new pricing to all active subscriptions for this package
                    </DialogDescription>
                  </DialogHeader>
                  
                  {!updateResults ? (
                    <div className="space-y-4 mt-4">
                      {updatingPackage && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h3 className="font-semibold text-blue-900 mb-2">Package: {updatingPackage.name}</h3>
                          <div className="text-sm text-blue-800 space-y-1">
                            <p>• Monthly Price: ${updatingPackage.monthly_price.toFixed(2)}</p>
                            <p>• Yearly Price: ${updatingPackage.yearly_price.toFixed(2)}</p>
                          </div>
                        </div>
                      )}
                      
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <h4 className="font-semibold text-amber-900 mb-2">⚠️ Important Information</h4>
                        <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
                          <li>This will update ALL active subscriptions for this package</li>
                          <li>New prices will apply at the next renewal (not immediately)</li>
                          <li>Current billing cycle will not be affected</li>
                          <li>Users will receive email notifications about the price change</li>
                        </ul>
                      </div>
                      
                      <div className="flex gap-3">
                        <Button 
                          variant="outline" 
                          onClick={() => setShowUpdateSubscriptionsDialog(false)}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleUpdateSubscriptions}
                          className="flex-1"
                          data-testid="confirm-update-subs"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Update Subscriptions
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4 mt-4">
                      <div className={`rounded-lg p-4 ${
                        updateResults.updated_count > 0 ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
                      }`}>
                        <h3 className="font-semibold mb-2">Update Results</h3>
                        <div className="space-y-1 text-sm">
                          <p>✅ Successfully Updated: {updateResults.updated_count}</p>
                          {updateResults.failed_count > 0 && (
                            <p className="text-red-600">❌ Failed: {updateResults.failed_count}</p>
                          )}
                        </div>
                      </div>
                      
                      {updateResults.details && updateResults.details.length > 0 && (
                        <div className="border rounded-lg max-h-64 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="text-left p-2 font-semibold">User Email</th>
                                <th className="text-left p-2 font-semibold">Status</th>
                                <th className="text-right p-2 font-semibold">Old Price</th>
                                <th className="text-right p-2 font-semibold">New Price</th>
                              </tr>
                            </thead>
                            <tbody>
                              {updateResults.details.map((detail, idx) => (
                                <tr key={idx} className="border-t">
                                  <td className="p-2">{detail.user_email}</td>
                                  <td className="p-2">
                                    <span className={`px-2 py-1 rounded text-xs ${
                                      detail.status === 'success' 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-red-100 text-red-800'
                                    }`}>
                                      {detail.status}
                                    </span>
                                  </td>
                                  <td className="p-2 text-right">
                                    {detail.old_price !== undefined ? `$${detail.old_price.toFixed(2)}` : '-'}
                                  </td>
                                  <td className="p-2 text-right">
                                    {detail.new_price !== undefined ? `$${detail.new_price.toFixed(2)}` : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      
                      <Button 
                        onClick={() => {
                          setShowUpdateSubscriptionsDialog(false);
                          setUpdateResults(null);
                        }}
                        className="w-full"
                      >
                        Close
                      </Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        );

      case 'product-terms':
        return (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Product Terms & Conditions</CardTitle>
                  <CardDescription>Manage terms for specific products</CardDescription>
                </div>
                <Dialog open={showProductTermsDialog} onOpenChange={setShowProductTermsDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-product-terms-button">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Product Terms
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Create/Update Product Terms</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Select Product</Label>
                        <Select
                          value={productTermsForm.product_id}
                          onValueChange={(value) => setProductTermsForm({ ...productTermsForm, product_id: value })}
                        >
                          <SelectTrigger data-testid="product-terms-select">
                            <SelectValue placeholder="Select a product" />
                          </SelectTrigger>
                          <SelectContent>
                            {products.map((product) => (
                              <SelectItem key={product.id} value={product.name}>
                                {product.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Terms Content</Label>
                        <Textarea
                          data-testid="product-terms-content-input"
                          value={productTermsForm.content}
                          onChange={(e) => setProductTermsForm({ ...productTermsForm, content: e.target.value })}
                          placeholder="Enter terms and conditions..."
                          rows={10}
                        />
                      </div>
                      <Button data-testid="save-product-terms-button" onClick={handleSaveProductTerms} className="w-full">
                        Save Product Terms
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {productTerms.map((term) => (
                  <Card key={term.id}>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-bold mb-2">Product: {term.product_id}</h3>
                          <p className="text-sm text-gray-600 whitespace-pre-wrap">{term.content.substring(0, 200)}...</p>
                        </div>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteProductTerms(term.id)}
                          data-testid={`delete-terms-${term.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {productTerms.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No product terms yet. Add terms for your products!</p>
                )}
              </div>
            </CardContent>
          </Card>
        );

      case 'page-terms':
        return (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Page Terms & Conditions</CardTitle>
                  <CardDescription>General terms displayed on the public page (users must accept on first visit)</CardDescription>
                </div>
                <Dialog open={showPageTermsDialog} onOpenChange={setShowPageTermsDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="edit-page-terms-button">
                      <Pencil className="w-4 h-4 mr-2" />
                      {pageTerms ? 'Edit Page Terms' : 'Add Page Terms'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Page Terms & Conditions</DialogTitle>
                      <DialogDescription>These terms will be shown to users on their first visit. They must accept to continue.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Terms Content</Label>
                        <Textarea
                          data-testid="page-terms-content-input"
                          value={pageTermsForm.content}
                          onChange={(e) => setPageTermsForm({ ...pageTermsForm, content: e.target.value })}
                          placeholder="Enter general terms and conditions for the page..."
                          rows={15}
                        />
                      </div>
                      <Button data-testid="save-page-terms-button" onClick={handleSavePageTerms} className="w-full">
                        Save Page Terms
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {pageTerms ? (
                <Card>
                  <CardContent className="p-6">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{pageTerms.content}</p>
                  </CardContent>
                </Card>
              ) : (
                <p className="text-center text-gray-500 py-8">No page terms set. Add general terms for your page!</p>
              )}
            </CardContent>
          </Card>
        );

      case 'email-templates':
        return (
          <EmailTemplatesSection
            emailTemplates={emailTemplates}
            configuredLanguages={configuredLanguages}
            editingTemplate={editingTemplate}
            setEditingTemplate={setEditingTemplate}
            showTemplateDialog={showTemplateDialog}
            setShowTemplateDialog={setShowTemplateDialog}
            showPreviewDialog={showPreviewDialog}
            setShowPreviewDialog={setShowPreviewDialog}
            previewTemplate={previewTemplate}
            handleInitializeTemplates={handleInitializeTemplates}
            handleEditTemplate={handleEditTemplate}
            handleSaveTemplate={handleSaveTemplate}
            handlePreviewTemplate={handlePreviewTemplate}
          />
        );

      case 'settings':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Settings</CardTitle>
              <CardDescription>Configure your Stripe and SMTP credentials</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold mb-4">Stripe Configuration</h3>
                  <div className="space-y-4">
                    <div>
                      <Label>Stripe Publishable Key</Label>
                      <Input
                        data-testid="stripe-publishable-key-input"
                        type="text"
                        value={settingsForm.stripe_publishable_key}
                        onChange={(e) => setSettingsForm({ ...settingsForm, stripe_publishable_key: e.target.value })}
                        placeholder="pk_test_..."
                      />
                      <p className="text-xs text-gray-500 mt-1">Used on frontend for Stripe Elements (safe to expose)</p>
                    </div>
                    <div>
                      <Label>Stripe Secret Key</Label>
                      <Input
                        data-testid="stripe-secret-key-input"
                        type="password"
                        value={settingsForm.stripe_secret_key}
                        onChange={(e) => setSettingsForm({ ...settingsForm, stripe_secret_key: e.target.value })}
                        placeholder="sk_test_..."
                      />
                      <p className="text-xs text-gray-500 mt-1">Used on backend for API calls (keep secret)</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">SMTP Configuration</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>SMTP Host</Label>
                        <Input
                          data-testid="smtp-host-input"
                          value={settingsForm.smtp_host}
                          onChange={(e) => setSettingsForm({ ...settingsForm, smtp_host: e.target.value })}
                          placeholder="smtp.gmail.com"
                        />
                      </div>
                      <div>
                        <Label>SMTP Port</Label>
                        <Input
                          data-testid="smtp-port-input"
                          type="number"
                          value={settingsForm.smtp_port}
                          onChange={(e) => setSettingsForm({ ...settingsForm, smtp_port: parseInt(e.target.value) })}
                          placeholder="587"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>SMTP Username</Label>
                      <Input
                        data-testid="smtp-username-input"
                        value={settingsForm.smtp_username}
                        onChange={(e) => setSettingsForm({ ...settingsForm, smtp_username: e.target.value })}
                        placeholder="your_email@gmail.com"
                      />
                    </div>
                    <div>
                      <Label>SMTP Password</Label>
                      <Input
                        data-testid="smtp-password-input"
                        type="password"
                        value={settingsForm.smtp_password}
                        onChange={(e) => setSettingsForm({ ...settingsForm, smtp_password: e.target.value })}
                        placeholder="App password"
                      />
                      <p className="text-xs text-gray-500 mt-1">Use app password for Gmail</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>From Email</Label>
                        <Input
                          data-testid="smtp-from-email-input"
                          value={settingsForm.smtp_from_email}
                          onChange={(e) => setSettingsForm({ ...settingsForm, smtp_from_email: e.target.value })}
                          placeholder="noreply@example.com"
                        />
                      </div>
                      <div>
                        <Label>From Name</Label>
                        <Input
                          data-testid="smtp-from-name-input"
                          value={settingsForm.smtp_from_name}
                          onChange={(e) => setSettingsForm({ ...settingsForm, smtp_from_name: e.target.value })}
                          placeholder="PayPulse Subscriptions"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4">Company Details (Invoice)</h3>
                  <div className="space-y-4">
                    <div>
                      <Label>Company Name</Label>
                      <Input
                        data-testid="company-name-input"
                        value={settingsForm.company_name}
                        onChange={(e) => setSettingsForm({ ...settingsForm, company_name: e.target.value })}
                        placeholder="Your Company Inc."
                      />
                    </div>
                    <div>
                      <Label>Company Logo URL</Label>
                      <Input
                        data-testid="company-logo-url-input"
                        value={settingsForm.company_logo_url}
                        onChange={(e) => setSettingsForm({ ...settingsForm, company_logo_url: e.target.value })}
                        placeholder="https://example.com/logo.png"
                      />
                      <p className="text-xs text-gray-500 mt-1">Provide a URL to your company logo</p>
                    </div>
                    <div>
                      <Label>Street Address</Label>
                      <Input
                        data-testid="company-street-input"
                        value={settingsForm.company_street}
                        onChange={(e) => setSettingsForm({ ...settingsForm, company_street: e.target.value })}
                        placeholder="123 Business Street"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>City</Label>
                        <Input
                          data-testid="company-city-input"
                          value={settingsForm.company_city}
                          onChange={(e) => setSettingsForm({ ...settingsForm, company_city: e.target.value })}
                          placeholder="New York"
                        />
                      </div>
                      <div>
                        <Label>State</Label>
                        <Input
                          data-testid="company-state-input"
                          value={settingsForm.company_state}
                          onChange={(e) => setSettingsForm({ ...settingsForm, company_state: e.target.value })}
                          placeholder="NY"
                        />
                      </div>
                      <div>
                        <Label>ZIP Code</Label>
                        <Input
                          data-testid="company-zip-input"
                          value={settingsForm.company_zip}
                          onChange={(e) => setSettingsForm({ ...settingsForm, company_zip: e.target.value })}
                          placeholder="10001"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Country</Label>
                      <Input
                        data-testid="company-country-input"
                        value={settingsForm.company_country}
                        onChange={(e) => setSettingsForm({ ...settingsForm, company_country: e.target.value })}
                        placeholder="United States"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Company Email</Label>
                        <Input
                          data-testid="company-email-input"
                          value={settingsForm.company_email}
                          onChange={(e) => setSettingsForm({ ...settingsForm, company_email: e.target.value })}
                          placeholder="contact@company.com"
                        />
                      </div>
                      <div>
                        <Label>Company Phone</Label>
                        <Input
                          data-testid="company-phone-input"
                          value={settingsForm.company_phone}
                          onChange={(e) => setSettingsForm({ ...settingsForm, company_phone: e.target.value })}
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Tax ID / Registration Number</Label>
                      <Input
                        data-testid="company-tax-id-input"
                        value={settingsForm.company_tax_id}
                        onChange={(e) => setSettingsForm({ ...settingsForm, company_tax_id: e.target.value })}
                        placeholder="EIN: 12-3456789"
                      />
                      <p className="text-xs text-gray-500 mt-1">Company tax ID or registration number (e.g., EIN, VAT)</p>
                    </div>
                  </div>
                </div>

                <Button
                  data-testid="save-settings-button"
                  onClick={handleSaveSettings}
                  className="w-full"
                >
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        );

      case 'countries':
        return (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Country Requirements</CardTitle>
                  <CardDescription>Set country-specific digital ID requirements</CardDescription>
                </div>
                <Dialog open={showCountryDialog} onOpenChange={(open) => {
                  setShowCountryDialog(open);
                  if (!open) {
                    setEditingCountry(null);
                    setCountryForm({
                      country_code: '',
                      country_name: '',
                      language_code: 'en',
                      language_name: 'English',
                      requires_digital_id: false,
                      digital_id_type: ''
                    });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-country-button">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Country
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingCountry ? 'Edit Country Requirement' : 'Add Country Requirement'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Country Code</Label>
                        <Input
                          data-testid="country-code-input"
                          value={countryForm.country_code}
                          onChange={(e) => setCountryForm({ ...countryForm, country_code: e.target.value })}
                          placeholder="US"
                        />
                      </div>
                      <div>
                        <Label>Country Name</Label>
                        <Input
                          data-testid="country-name-input"
                          value={countryForm.country_name}
                          onChange={(e) => setCountryForm({ ...countryForm, country_name: e.target.value })}
                          placeholder="United States"
                        />
                      </div>
                      <div>
                        <Label>Select Language</Label>
                        <Select
                          value={`${countryForm.language_code}|${countryForm.language_name}`}
                          onValueChange={(value) => {
                            const [code, name] = value.split('|');
                            setCountryForm({ 
                              ...countryForm, 
                              language_code: code,
                              language_name: name
                            });
                          }}
                        >
                          <SelectTrigger data-testid="language-select">
                            <SelectValue placeholder="Select a language" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            <SelectItem value="en|English">English (en)</SelectItem>
                            <SelectItem value="es|Spanish">Spanish (es)</SelectItem>
                            <SelectItem value="fr|French">French (fr)</SelectItem>
                            <SelectItem value="pt|Portuguese">Portuguese (pt)</SelectItem>
                            <SelectItem value="de|German">German (de)</SelectItem>
                            <SelectItem value="it|Italian">Italian (it)</SelectItem>
                            <SelectItem value="ja|Japanese">Japanese (ja)</SelectItem>
                            <SelectItem value="zh|Chinese">Chinese (zh)</SelectItem>
                            <SelectItem value="ar|Arabic">Arabic (ar)</SelectItem>
                            <SelectItem value="hi|Hindi">Hindi (hi)</SelectItem>
                            <SelectItem value="ru|Russian">Russian (ru)</SelectItem>
                            <SelectItem value="ko|Korean">Korean (ko)</SelectItem>
                            <SelectItem value="nl|Dutch">Dutch (nl)</SelectItem>
                            <SelectItem value="sv|Swedish">Swedish (sv)</SelectItem>
                            <SelectItem value="pl|Polish">Polish (pl)</SelectItem>
                            <SelectItem value="tr|Turkish">Turkish (tr)</SelectItem>
                            <SelectItem value="vi|Vietnamese">Vietnamese (vi)</SelectItem>
                            <SelectItem value="th|Thai">Thai (th)</SelectItem>
                            <SelectItem value="id|Indonesian">Indonesian (id)</SelectItem>
                            <SelectItem value="ms|Malay">Malay (ms)</SelectItem>
                            <SelectItem value="fil|Filipino">Filipino (fil)</SelectItem>
                            <SelectItem value="da|Danish">Danish (da)</SelectItem>
                            <SelectItem value="fi|Finnish">Finnish (fi)</SelectItem>
                            <SelectItem value="no|Norwegian">Norwegian (no)</SelectItem>
                            <SelectItem value="el|Greek">Greek (el)</SelectItem>
                            <SelectItem value="he|Hebrew">Hebrew (he)</SelectItem>
                            <SelectItem value="cs|Czech">Czech (cs)</SelectItem>
                            <SelectItem value="ro|Romanian">Romanian (ro)</SelectItem>
                            <SelectItem value="hu|Hungarian">Hungarian (hu)</SelectItem>
                            <SelectItem value="uk|Ukrainian">Ukrainian (uk)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500 mt-1">
                          Selected: {countryForm.language_name} ({countryForm.language_code})
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          data-testid="requires-digital-id-checkbox"
                          checked={countryForm.requires_digital_id}
                          onCheckedChange={(checked) => setCountryForm({ ...countryForm, requires_digital_id: checked })}
                        />
                        <Label>Requires Digital ID</Label>
                      </div>
                      {countryForm.requires_digital_id && (
                        <div>
                          <Label>Digital ID Type</Label>
                          <Input
                            data-testid="digital-id-type-input"
                            value={countryForm.digital_id_type}
                            onChange={(e) => setCountryForm({ ...countryForm, digital_id_type: e.target.value })}
                            placeholder="e.g., Passport, DNI, National ID, Driver's License"
                          />
                          <p className="text-xs text-gray-500 mt-1">Enter the type of digital ID required for this country</p>
                        </div>
                      )}
                      <Button data-testid="save-country-button" onClick={handleSaveCountryRequirement} className="w-full">
                        Save Country
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {countryRequirements.map((country) => (
                  <Card key={country.id}>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <h3 className="font-bold">{country.country_name} ({country.country_code})</h3>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Language:</span> {country.language_name || 'English'} ({country.language_code || 'en'})
                          </p>
                          <p className="text-sm text-gray-600">
                            {country.requires_digital_id
                              ? `Requires ${country.digital_id_type}`
                              : 'No digital ID required'}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditCountry(country)}
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteCountry(country.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {countryRequirements.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No country requirements yet. Add countries!</p>
                )}
              </div>
            </CardContent>
          </Card>
        );

      case 'transactions':
        return (
          <div className="space-y-6">
            {/* Sub-navigation tabs */}
            <div className="flex gap-2 border-b">
              <button
                onClick={() => setTransactionsSubTab('list')}
                className={`px-4 py-2 font-medium text-sm ${
                  transactionsSubTab === 'list'
                    ? 'border-b-2 border-gray-900 text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Transaction List
              </button>
              <button
                onClick={() => setTransactionsSubTab('upcoming')}
                className={`px-4 py-2 font-medium text-sm ${
                  transactionsSubTab === 'upcoming'
                    ? 'border-b-2 border-gray-900 text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Upcoming Payments
              </button>
              <button
                onClick={() => setTransactionsSubTab('users')}
                className={`px-4 py-2 font-medium text-sm ${
                  transactionsSubTab === 'users'
                    ? 'border-b-2 border-gray-900 text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Users by Status
              </button>
            </div>

            {/* Transaction List Tab */}
            {transactionsSubTab === 'list' && (
              <Card>
                <CardHeader>
                  <CardTitle>Transactions</CardTitle>
                  <CardDescription>Filter and view payment transactions</CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Filters */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg space-y-4">
                    <h3 className="font-semibold text-sm">Filters</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                      <div>
                        <Label className="text-xs">Package</Label>
                        <Select
                          value={transactionFilters.package_id}
                          onValueChange={(value) => setTransactionFilters({...transactionFilters, package_id: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="All Packages" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Packages</SelectItem>
                            {filterOptions.packages.map((pkg) => (
                              <SelectItem key={pkg.id} value={pkg.id}>{pkg.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">User Country</Label>
                        <Select
                          value={transactionFilters.user_country}
                          onValueChange={(value) => setTransactionFilters({...transactionFilters, user_country: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="All Countries" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Countries</SelectItem>
                            {filterOptions.user_countries.map((country) => (
                              <SelectItem key={country} value={country}>{country}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">IP Country</Label>
                        <Select
                          value={transactionFilters.ip_country}
                          onValueChange={(value) => setTransactionFilters({...transactionFilters, ip_country: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="All Countries" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Countries</SelectItem>
                            {filterOptions.ip_countries.map((country) => (
                              <SelectItem key={country} value={country}>{country}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Date From</Label>
                        <Input
                          type="date"
                          value={transactionFilters.date_from}
                          onChange={(e) => setTransactionFilters({...transactionFilters, date_from: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Date To</Label>
                        <Input
                          type="date"
                          value={transactionFilters.date_to}
                          onChange={(e) => setTransactionFilters({...transactionFilters, date_to: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={applyFilters} size="sm">Apply Filters</Button>
                      <Button onClick={clearFilters} size="sm" variant="outline">Clear Filters</Button>
                    </div>
                  </div>

                  {/* Transactions Table */}
                  {transactions.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No transactions found.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-3 text-sm font-semibold">Customer</th>
                            <th className="text-left p-3 text-sm font-semibold">Package</th>
                            <th className="text-left p-3 text-sm font-semibold">Amount</th>
                            <th className="text-left p-3 text-sm font-semibold">Billing</th>
                            <th className="text-left p-3 text-sm font-semibold">User Country</th>
                            <th className="text-left p-3 text-sm font-semibold">IPv4</th>
                            <th className="text-left p-3 text-sm font-semibold">IP Country</th>
                            <th className="text-left p-3 text-sm font-semibold">Status</th>
                            <th className="text-left p-3 text-sm font-semibold">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map((transaction) => {
                            const subscription = subscriptions.find(s => s.id === transaction.subscription_id);
                            return (
                              <tr key={transaction.id} className="border-b hover:bg-gray-50">
                                <td className="p-3 text-sm">
                                  <div>
                                    <p className="font-medium">{subscription?.user_name || 'N/A'}</p>
                                    <p className="text-gray-500 text-xs">{transaction.user_email}</p>
                                  </div>
                                </td>
                                <td className="p-3 text-sm">{transaction.metadata?.package_name || 'N/A'}</td>
                                <td className="p-3 text-sm">
                                  <p className="font-medium">${transaction.amount.toFixed(2)}</p>
                                  <p className="text-gray-500 text-xs">{transaction.currency.toUpperCase()}</p>
                                </td>
                                <td className="p-3 text-sm capitalize">{transaction.billing_cycle}</td>
                                <td className="p-3 text-sm">{subscription?.country_code || 'N/A'}</td>
                                <td className="p-3 text-sm font-mono text-xs">{transaction.ip_address || 'N/A'}</td>
                                <td className="p-3 text-sm">{transaction.country || 'N/A'}</td>
                                <td className="p-3 text-sm">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    transaction.payment_status === 'paid'
                                      ? 'bg-green-100 text-green-800'
                                      : transaction.payment_status === 'initiated'
                                      ? 'bg-blue-100 text-blue-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {transaction.payment_status}
                                  </span>
                                </td>
                                <td className="p-3 text-sm text-gray-500">
                                  {new Date(transaction.created_at).toLocaleDateString()}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Upcoming Payments Tab */}
            {transactionsSubTab === 'upcoming' && (
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle>Upcoming Payments</CardTitle>
                      <CardDescription>Users with payments due in the next {upcomingDays} days</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      {[7, 10, 14, 30].map((days) => (
                        <Button
                          key={days}
                          size="sm"
                          variant={upcomingDays === days ? 'default' : 'outline'}
                          onClick={() => setUpcomingDays(days)}
                        >
                          {days} Days
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {upcomingPayments.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No upcoming payments in the next {upcomingDays} days.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-3 text-sm font-semibold">User</th>
                            <th className="text-left p-3 text-sm font-semibold">Package</th>
                            <th className="text-left p-3 text-sm font-semibold">Amount</th>
                            <th className="text-left p-3 text-sm font-semibold">Billing</th>
                            <th className="text-left p-3 text-sm font-semibold">Country</th>
                            <th className="text-left p-3 text-sm font-semibold">Next Payment</th>
                            <th className="text-left p-3 text-sm font-semibold">Days Until</th>
                          </tr>
                        </thead>
                        <tbody>
                          {upcomingPayments.map((payment, idx) => (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="p-3 text-sm">
                                <div>
                                  <p className="font-medium">{payment.user_name}</p>
                                  <p className="text-gray-500 text-xs">{payment.user_email}</p>
                                </div>
                              </td>
                              <td className="p-3 text-sm">{payment.package_name}</td>
                              <td className="p-3 text-sm">
                                <p className="font-medium">${payment.amount.toFixed(2)}</p>
                                <p className="text-gray-500 text-xs">{payment.currency}</p>
                              </td>
                              <td className="p-3 text-sm capitalize">{payment.billing_cycle}</td>
                              <td className="p-3 text-sm">{payment.country_code}</td>
                              <td className="p-3 text-sm">
                                {new Date(payment.next_billing_date).toLocaleDateString()}
                              </td>
                              <td className="p-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  payment.days_until_payment <= 3
                                    ? 'bg-red-100 text-red-800'
                                    : payment.days_until_payment <= 7
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {payment.days_until_payment} days
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Users by Status Tab */}
            {transactionsSubTab === 'users' && (
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle>Users by Subscription Status</CardTitle>
                      <CardDescription>View users grouped by subscription status</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={usersStatusFilter === 'all' ? 'default' : 'outline'}
                        onClick={() => setUsersStatusFilter('all')}
                      >
                        All Users
                      </Button>
                      <Button
                        size="sm"
                        variant={usersStatusFilter === 'active' ? 'default' : 'outline'}
                        onClick={() => setUsersStatusFilter('active')}
                      >
                        Active Only
                      </Button>
                      <Button
                        size="sm"
                        variant={usersStatusFilter === 'inactive' ? 'default' : 'outline'}
                        onClick={() => setUsersStatusFilter('inactive')}
                      >
                        Inactive Only
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {usersByStatus.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No users found.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-3 text-sm font-semibold">User</th>
                            <th className="text-left p-3 text-sm font-semibold">Country</th>
                            <th className="text-center p-3 text-sm font-semibold">Total Subs</th>
                            <th className="text-center p-3 text-sm font-semibold">Active</th>
                            <th className="text-center p-3 text-sm font-semibold">Inactive</th>
                            <th className="text-left p-3 text-sm font-semibold">Subscription Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usersByStatus.map((user, idx) => (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="p-3 text-sm">
                                <div>
                                  <p className="font-medium">{user.user_name}</p>
                                  <p className="text-gray-500 text-xs">{user.user_email}</p>
                                </div>
                              </td>
                              <td className="p-3 text-sm">{user.country_code}</td>
                              <td className="p-3 text-sm text-center">
                                <span className="font-semibold">{user.total_subscriptions}</span>
                              </td>
                              <td className="p-3 text-sm text-center">
                                <span className="text-green-600 font-medium">{user.active_subscriptions}</span>
                              </td>
                              <td className="p-3 text-sm text-center">
                                <span className="text-gray-500">{user.inactive_subscriptions}</span>
                              </td>
                              <td className="p-3 text-sm">
                                <div className="space-y-2">
                                  {user.subscriptions.map((sub, subIdx) => (
                                    <div key={subIdx} className="flex items-center gap-2 text-xs">
                                      <span className="font-medium">{sub.package_name}</span>
                                      <span className="text-gray-400">•</span>
                                      <span className="capitalize text-gray-600">{sub.billing_cycle}</span>
                                      <span className="text-gray-400">•</span>
                                      <span className="font-medium">${sub.amount}</span>
                                      <span className="text-gray-400">•</span>
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                        sub.status === 'active'
                                          ? 'bg-green-100 text-green-800'
                                          : 'bg-gray-100 text-gray-800'
                                      }`}>
                                        {sub.status}
                                      </span>
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                        sub.payment_status === 'paid'
                                          ? 'bg-blue-100 text-blue-800'
                                          : 'bg-yellow-100 text-yellow-800'
                                      }`}>
                                        {sub.payment_status}
                                      </span>
                                      {sub.next_billing_date && (
                                        <>
                                          <span className="text-gray-400">•</span>
                                          <span className="text-gray-500">
                                            Next: {new Date(sub.next_billing_date).toLocaleDateString()}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 'terms-acceptances':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Terms Acceptances</CardTitle>
              <CardDescription>View all page terms acceptances by users</CardDescription>
            </CardHeader>
            <CardContent>
              {termsAcceptances.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No terms acceptances yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 text-sm font-semibold">User Email</th>
                        <th className="text-left p-3 text-sm font-semibold">IP Address</th>
                        <th className="text-left p-3 text-sm font-semibold">Country</th>
                        <th className="text-left p-3 text-sm font-semibold">User Agent</th>
                        <th className="text-left p-3 text-sm font-semibold">Accepted At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {termsAcceptances.map((acceptance) => (
                        <tr key={acceptance.id} className="border-b hover:bg-gray-50">
                          <td className="p-3 text-sm font-medium">
                            {acceptance.user_email || 'Anonymous'}
                          </td>
                          <td className="p-3 text-sm font-mono text-xs">
                            {acceptance.ip_address || 'N/A'}
                          </td>
                          <td className="p-3 text-sm">
                            {acceptance.country || 'N/A'}
                          </td>
                          <td className="p-3 text-sm text-gray-600 max-w-xs truncate">
                            {acceptance.user_agent || 'N/A'}
                          </td>
                          <td className="p-3 text-sm text-gray-500">
                            {new Date(acceptance.accepted_at).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center">
            <Home className="w-6 h-6 text-gray-900 mr-3" />
            <h1 className="text-xl font-semibold text-gray-900">Admin Dashboard</h1>
          </div>
          <Button
            data-testid="logout-button"
            onClick={handleLogout}
            variant="outline"
            className="border-gray-300"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-white border-r border-gray-200 min-h-screen">
          <nav className="p-4 space-y-1">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'dashboard'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <BarChart3 className="w-5 h-5 mr-3" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('products')}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'products'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Box className="w-5 h-5 mr-3" />
              Products
            </button>
            <button
              onClick={() => setActiveTab('packages')}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'packages'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Package className="w-5 h-5 mr-3" />
              Packages
            </button>
            <button
              onClick={() => setActiveTab('product-terms')}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'product-terms'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FileText className="w-5 h-5 mr-3" />
              Product Terms
            </button>
            <button
              onClick={() => setActiveTab('page-terms')}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'page-terms'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FileCheck className="w-5 h-5 mr-3" />
              Page Terms
            </button>
            <button
              onClick={() => setActiveTab('countries')}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'countries'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Globe className="w-5 h-5 mr-3" />
              Countries
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'transactions'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Receipt className="w-5 h-5 mr-3" />
              Transactions
            </button>
            <button
              onClick={() => setActiveTab('terms-acceptances')}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'terms-acceptances'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FileCheck className="w-5 h-5 mr-3" />
              Terms Acceptances
            </button>
            <button
              onClick={() => setActiveTab('email-templates')}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'email-templates'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Mail className="w-5 h-5 mr-3" />
              Email Templates
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'settings'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <SettingsIcon className="w-5 h-5 mr-3" />
              Settings
            </button>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
